import { ChevronLeft, ChevronRight, Image as ImageIcon, Loader2, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../components/ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';

export function EnhanceProductModal({
	open,
	onOpenChange,
	onConfirm,
	loading,
	productName,
	originalImages,
	aiPreviewImage,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onConfirm: () => void;
	loading?: boolean;
	productName: string;
	originalImages: Array<{ url: string; isPrimary?: boolean }>;
	aiPreviewImage?: string;
}) {
	const [imgIdx, setImgIdx] = useState(0);
	const current = originalImages[imgIdx];

	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				if (!next) setImgIdx(0);
				onOpenChange(next);
			}}
		>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>Enhance with Design Studio</DialogTitle>
					<DialogDescription>
						Transform <strong className="text-text-1">{productName}</strong> using
						AI-powered design enhancements. Review the comparison below.
					</DialogDescription>
				</DialogHeader>

				<DialogBody>
					<div className="grid grid-cols-2 gap-4">
						{/* Original */}
						<div>
							<div className="mb-2 text-xs font-medium uppercase tracking-[0.06em] text-text-4">
								Original ({imgIdx + 1}/{originalImages.length})
							</div>
							<div className="relative aspect-square overflow-hidden rounded-lg border border-line-1 bg-surface-fill">
								{current?.url ? (
									<img
										src={current.url}
										alt="Original"
										className="h-full w-full object-contain"
									/>
								) : (
									<div className="flex h-full w-full items-center justify-center">
										<ImageIcon size={24} className="text-text-4" />
									</div>
								)}
								{originalImages.length > 1 && (
									<>
										<button
											onClick={() =>
												setImgIdx((i) =>
													i === 0 ? originalImages.length - 1 : i - 1
												)
											}
											className="absolute left-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/50 text-white/70 hover:bg-black/70"
										>
											<ChevronLeft size={14} />
										</button>
										<button
											onClick={() =>
												setImgIdx((i) =>
													i === originalImages.length - 1 ? 0 : i + 1
												)
											}
											className="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/50 text-white/70 hover:bg-black/70"
										>
											<ChevronRight size={14} />
										</button>
									</>
								)}
							</div>
						</div>

						{/* AI Preview */}
						<div>
							<div className="mb-2 flex items-center gap-1.5 text-xs font-medium uppercase tracking-[0.06em] text-text-4">
								<Sparkles size={11} />
								AI Preview
							</div>
							<div className="relative flex aspect-square items-center justify-center overflow-hidden rounded-lg border border-line-1 bg-surface-fill">
								{aiPreviewImage ? (
									<img
										src={aiPreviewImage}
										alt="AI preview"
										className="h-full w-full object-contain"
									/>
								) : (
									<div className="flex flex-col items-center gap-2">
										<Loader2 size={20} className="animate-spin text-text-4" />
										<span className="text-xs text-text-4">Generating…</span>
									</div>
								)}
							</div>
						</div>
					</div>
				</DialogBody>

				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
						disabled={loading}
					>
						Cancel
					</Button>
					<Button onClick={onConfirm} disabled={loading}>
						{loading ? 'Enhancing…' : 'Enhance product'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
